if (!customElements.get('cake-atelier')) {
  customElements.define('cake-atelier', class CakeAtelier extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.cake = this.querySelector('.cake-atelier__cake');
      this.decal = this.querySelector('.cake-atelier__decal');
      this.stage = this.querySelector('.cake-atelier__stage');
      this.canvas = this.querySelector('[data-cake-canvas]');
      this.placementLayer = this.querySelector('[data-placement-layer]');
      this.placementBox = this.querySelector('[data-placement-box]');
      this.imageList = this.querySelector('[data-image-list]');
      this.swatchToggle = this.querySelector('[data-swatch-toggle]');
      this.copy = this.loadCopy();
      this.images = [];
      this.activeImageId = null;
      this.imageIdCounter = 0;
      this.finishesExpanded = false;
      this.configuration = null;
      this.cake3d = null;
      this.placement = { tier: 'top', surface: 'front', x: 0, y: 0, scale: 0.42, rotation: 0 };
      this.placementDrag = null;
      const dateField = this.form.elements.date;
      if (dateField) {
        dateField.addEventListener('input', () => this.formatDateField(dateField));
        dateField.addEventListener('blur', () => this.validateDateField(dateField));
      }
      this.form.addEventListener('change', (event) => this.handleChange(event));
      this.form.addEventListener('input', () => this.updatePreview());
      this.form.addEventListener('submit', (event) => this.submit(event));
      if (this.swatchToggle) this.swatchToggle.addEventListener('click', () => this.toggleFinishes());
      this.querySelectorAll('[data-category]').forEach((button) => button.addEventListener('click', () => {
        this.form.elements.category.value = button.dataset.category;
        this.updatePreview();
        this.querySelector('.cake-atelier__designer').scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }));
      this.setupPlacementControls();
      this.updatePreview();
      this.loadConfiguration();
      this.init3D();
    }

    disconnectedCallback() {
      this.images.forEach((image) => URL.revokeObjectURL(image.url));
      if (this.cake3d) this.cake3d.dispose();
      window.removeEventListener('pointermove', this.boundPlacementMove);
      window.removeEventListener('pointerup', this.boundPlacementEnd);
    }

    loadCopy() {
      const source = this.querySelector('[data-cake-atelier-translations]');
      if (!source) return {};
      try {
        return JSON.parse(source.textContent);
      } catch (error) {
        console.error('[Cake atelier translations]', error);
        return {};
      }
    }

    text(path, fallback = '', replacements = {}) {
      const value = path.split('.').reduce((current, key) => current && current[key], this.copy);
      const template = typeof value === 'string' ? value : fallback;
      return template.replace(/\{\{\s*(\w+)\s*\}\}|\[\[\s*(\w+)\s*\]\]/g, (_, liquidKey, bracketKey) => replacements[liquidKey || bracketKey] ?? '');
    }

    localizedMapValue(mapName, key, fallback = key) {
      const group = this.copy[mapName] || {};
      return group[key] || fallback;
    }

    measurement(value) {
      return `${value} ${this.text('units.cm', 'cm')}`;
    }

    localizeMeasurement(value) {
      return String(value || '').replace(/\bcm\b/g, this.text('units.cm', 'cm'));
    }

    selectedLabel(name) {
      const field = this.form.elements[name];
      if (!field) return '';
      if (field instanceof RadioNodeList) return this.labelForRadio(name);
      const option = field.selectedOptions && field.selectedOptions[0];
      return option ? (option.dataset.label || option.textContent || option.value) : field.value;
    }

    tierLabel(value = this.selected('tiers')) {
      if (String(value) === '1') return this.text('tiers.one', 'One tier');
      if (String(value) === '3') return this.text('tiers.three', 'Three tiers');
      return this.text('tiers.two', 'Two tiers');
    }

    tierCompoundLabel(value = this.selected('tiers')) {
      if (String(value) === '1') return this.text('tiers.one_compound', 'One-tier');
      if (String(value) === '3') return this.text('tiers.three_compound', 'Three-tier');
      return this.text('tiers.two_compound', 'Two-tier');
    }

    handleChange(event) {
      if (event.target.name === 'inspiration') this.handleImage(event.target);
      if (event.target.name === 'shape' && this.configuration) this.populateSizeOptions(event.target.value);
      if (event.target.name === 'shape' || event.target.name === 'tiers') this.syncTierOptions();
      if (event.target.name === 'image_tier') {
        this.setPlacement({ tier: event.target.value });
        this.saveActivePlacement();
      }
      if (event.target.name === 'image_surface') {
        this.setPlacement({ surface: event.target.value });
        this.saveActivePlacement();
        this.setCakeView(event.target.value === 'top' ? 'top' : 'front');
      }
      if (event.target.classList.contains('is-invalid')) event.target.classList.remove('is-invalid');
      this.updatePreview();
    }

    async init3D() {
      const status = this.querySelector('[data-canvas-status]');
      try {
        const [THREE, rendererModule] = await Promise.all([
          import(this.dataset.threeUrl),
          import(this.dataset.rendererUrl)
        ]);
        this.cake3d = new rendererModule.CakeAtelier3D({
          canvas: this.canvas,
          THREE,
          onPlacementFrame: (rect) => this.updatePlacementBox(rect)
        });
        this.stage.classList.add('is-3d-ready');
        this.updatePreview();
        for (const image of this.images) await this.cake3d.setImage(image);
        if (this.activeImageId) this.cake3d.setActiveImage(this.activeImageId);
      } catch (error) {
        status.textContent = this.text('ui.three_unavailable', 'Interactive 3D is unavailable on this device. Your selections will still appear in the visual preview.');
        console.error('[Cake atelier 3D]', error);
      }
    }

    setupPlacementControls() {
      this.querySelectorAll('[data-cake-view]').forEach((button) => button.addEventListener('click', () => this.setCakeView(button.dataset.cakeView)));
      this.querySelector('[data-placement-reset]').addEventListener('click', () => {
        this.placement = { ...this.placement, x: 0, y: 0, scale: 0.42, rotation: 0 };
        this.saveActivePlacement();
        this.updatePlacement();
      });
      this.querySelector('[data-placement-remove]').addEventListener('click', () => this.removeImage());
      this.querySelector('[data-placement-save]').addEventListener('click', () => this.savePreview());
      this.placementBox.addEventListener('pointerdown', (event) => this.startPlacementDrag(event));
      this.placementBox.addEventListener('keydown', (event) => this.handlePlacementKeydown(event));
      this.boundPlacementMove = (event) => this.movePlacement(event);
      this.boundPlacementEnd = (event) => this.endPlacementDrag(event);
      window.addEventListener('pointermove', this.boundPlacementMove);
      window.addEventListener('pointerup', this.boundPlacementEnd);
      this.syncTierOptions();
      this.updateDesignSpec();
    }

    setCakeView(view) {
      if (this.cake3d) this.cake3d.setView(view);
      this.querySelectorAll('[data-cake-view]').forEach((button) => button.classList.toggle('is-active', button.dataset.cakeView === view));
    }

    syncTierOptions() {
      const select = this.form.elements.image_tier;
      if (!select) return;
      const requestedTiers = this.selected('shape') === 'tart' ? 1 : Number(this.selected('tiers')) || 1;
      const labels = requestedTiers === 3
        ? [['top', this.text('tiers.top', 'Top tier')], ['middle', this.text('tiers.middle', 'Middle tier')], ['bottom', this.text('tiers.bottom', 'Bottom tier')]]
        : requestedTiers === 2
          ? [['top', this.text('tiers.top', 'Top tier')], ['bottom', this.text('tiers.bottom', 'Bottom tier')]]
          : [['top', this.text('tiers.only', 'Only tier')]];
      const current = labels.some(([value]) => value === this.placement.tier) ? this.placement.tier : 'top';
      select.replaceChildren(...labels.map(([value, label]) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        option.selected = value === current;
        return option;
      }));
      this.placement.tier = current;
      this.saveActivePlacement();
      this.updatePlacement();
    }

    activeImage() {
      return this.images.find((image) => image.id === this.activeImageId) || null;
    }

    hasImages() {
      return this.images.length > 0;
    }

    saveActivePlacement() {
      const image = this.activeImage();
      if (image) image.placement = { ...this.placement };
    }

    setActiveImage(id) {
      const image = this.images.find((item) => item.id === id);
      if (!image) return;
      this.saveActivePlacement();
      this.activeImageId = id;
      this.placement = { ...image.placement };
      if (this.cake3d) this.cake3d.setActiveImage(id);
      this.syncPlacementControls();
      this.renderImageList();
      this.updatePlacement();
      this.setCakeView(this.placement.surface === 'top' ? 'top' : 'front');
    }

    syncPlacementControls() {
      const tierSelect = this.form.elements.image_tier;
      if (tierSelect) tierSelect.value = this.placement.tier;
      const surfaceInput = this.form.querySelector(`input[name="image_surface"][value="${this.placement.surface}"]`);
      if (surfaceInput) surfaceInput.checked = true;
    }

    startPlacementDrag(event) {
      if (!this.hasImages()) return;
      event.preventDefault();
      event.stopPropagation();
      const action = event.target.dataset.placementAction || 'move';
      const rect = this.placementBox.getBoundingClientRect();
      this.placementDrag = {
        pointerId: event.pointerId,
        action,
        startX: event.clientX,
        startY: event.clientY,
        centerX: rect.left + rect.width / 2,
        centerY: rect.top + rect.height / 2,
        placement: { ...this.placement },
        startAngle: Math.atan2(event.clientY - (rect.top + rect.height / 2), event.clientX - (rect.left + rect.width / 2))
      };
      this.placementBox.setPointerCapture(event.pointerId);
    }

    movePlacement(event) {
      if (!this.placementDrag || this.placementDrag.pointerId !== event.pointerId) return;
      const drag = this.placementDrag;
      const stageRect = this.canvas.getBoundingClientRect();
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (drag.action === 'resize') {
        this.placement.scale = Math.max(0.12, Math.min(0.9, drag.placement.scale + (dx + dy) / Math.max(240, stageRect.width + stageRect.height) * 1.8));
      } else if (drag.action === 'rotate') {
        const angle = Math.atan2(event.clientY - drag.centerY, event.clientX - drag.centerX);
        this.placement.rotation = drag.placement.rotation + (angle - drag.startAngle) * 180 / Math.PI;
      } else {
        this.placement.x = Math.max(-0.5, Math.min(0.5, drag.placement.x + dx / Math.max(180, stageRect.width * 0.58)));
        this.placement.y = Math.max(-0.5, Math.min(0.5, drag.placement.y - dy / Math.max(180, stageRect.height * 0.46)));
      }
      this.saveActivePlacement();
      this.updatePlacement();
    }

    endPlacementDrag(event) {
      if (!this.placementDrag || this.placementDrag.pointerId !== event.pointerId) return;
      this.placementDrag = null;
    }

    handlePlacementKeydown(event) {
      const step = event.shiftKey ? 0.05 : 0.015;
      const changes = {};
      if (event.key === 'ArrowLeft') changes.x = this.placement.x - step;
      else if (event.key === 'ArrowRight') changes.x = this.placement.x + step;
      else if (event.key === 'ArrowUp') changes.y = this.placement.y + step;
      else if (event.key === 'ArrowDown') changes.y = this.placement.y - step;
      else if (event.key === '+' || event.key === '=') changes.scale = this.placement.scale + step;
      else if (event.key === '-') changes.scale = this.placement.scale - step;
      else if (event.key === '[') changes.rotation = this.placement.rotation - 5;
      else if (event.key === ']') changes.rotation = this.placement.rotation + 5;
      else return;
      event.preventDefault();
      this.setPlacement(changes);
    }

    setPlacement(changes) {
      this.placement = {
        ...this.placement,
        ...changes,
        x: Math.max(-0.5, Math.min(0.5, changes.x ?? this.placement.x)),
        y: Math.max(-0.5, Math.min(0.5, changes.y ?? this.placement.y)),
        scale: Math.max(0.12, Math.min(0.9, changes.scale ?? this.placement.scale))
      };
      this.saveActivePlacement();
      this.updatePlacement();
    }

    updatePlacement() {
      if (this.cake3d && this.activeImageId) this.cake3d.setPlacement(this.placement, this.activeImageId);
      this.updateDesignSpec();
    }

    updatePlacementBox(rect) {
      if (!this.hasImages() || !rect) {
        this.placementLayer.hidden = true;
        return;
      }
      this.placementLayer.hidden = false;
      this.placementBox.style.left = `${rect.left}px`;
      this.placementBox.style.top = `${rect.top}px`;
      this.placementBox.style.width = `${rect.width}px`;
      this.placementBox.style.height = `${rect.height}px`;
    }

    updateDesignSpec() {
      const field = this.querySelector('[data-design-spec]');
      if (!field) return;
      field.value = JSON.stringify({
        version: 1,
        images: this.images.map((image, index) => ({
          index: index + 1,
          name: image.name,
          tier: image.placement.tier,
          surface: image.placement.surface,
          x_percent: Math.round((image.placement.x + 0.5) * 100),
          y_percent: Math.round((0.5 - image.placement.y) * 100),
          size_percent: Math.round(image.placement.scale * 100),
          rotation_degrees: Math.round(image.placement.rotation)
        }))
      });
    }

    placementDescription() {
      if (!this.hasImages()) return this.text('whatsapp.placement_none', 'None');
      return this.images.map((image, index) => {
        const tier = image.placement.tier === 'top'
          ? this.text('tiers.top', 'Top tier')
          : image.placement.tier === 'middle'
            ? this.text('tiers.middle', 'Middle tier')
            : this.text('tiers.bottom', 'Bottom tier');
        return this.text('whatsapp.placement_line', '{{ index }}. {{ name }}: {{ tier }}, {{ surface }} surface, X {{ x }}%, Y {{ y }}%, size {{ size }}%, rotation {{ rotation }}°', {
          index: index + 1,
          name: image.name,
          tier,
          surface: this.localizedMapValue('surfaces', image.placement.surface, image.placement.surface),
          x: Math.round((image.placement.x + 0.5) * 100),
          y: Math.round((0.5 - image.placement.y) * 100),
          size: Math.round(image.placement.scale * 100),
          rotation: Math.round(image.placement.rotation)
        });
      }).join('; ');
    }

    savePreview() {
      if (!this.cake3d) return;
      const link = document.createElement('a');
      link.download = 'joy-cake-design.png';
      link.href = this.cake3d.exportPreview();
      link.click();
    }

    removeImage() {
      const image = this.activeImage();
      if (!image) return;
      const input = this.form.elements.inspiration;
      input.value = '';
      URL.revokeObjectURL(image.url);
      this.images = this.images.filter((item) => item.id !== image.id);
      if (this.cake3d) this.cake3d.removeImage(image.id);
      this.activeImageId = this.images.length ? this.images[this.images.length - 1].id : null;
      if (this.activeImageId) {
        const next = this.activeImage();
        this.placement = { ...next.placement };
        if (this.cake3d) this.cake3d.setActiveImage(this.activeImageId);
      }
      this.decal.style.backgroundImage = this.activeImage() ? `url("${this.activeImage().url}")` : '';
      this.querySelector('[data-placement-panel]').hidden = !this.hasImages();
      this.placementLayer.hidden = !this.hasImages();
      this.querySelector('.cake-atelier__upload-status').textContent = '';
      this.renderImageList();
      this.syncPlacementControls();
      this.updateDesignSpec();
    }

    async loadConfiguration() {
      try {
        const [configResponse, finishesResponse] = await Promise.all([
          fetch(this.dataset.configUrl),
          fetch(this.dataset.finishesUrl)
        ]);
        if (!configResponse.ok || !finishesResponse.ok) throw new Error('Cake configuration could not be loaded.');
        this.configuration = await configResponse.json();
        const finishData = await finishesResponse.json();
        this.renderFlavours();
        this.renderHeights();
        this.renderFinishes(finishData.finishes || []);
        this.populateSizeOptions(this.selected('shape'));
        this.updatePreview();
      } catch (error) {
        console.error('[Cake atelier]', error);
      }
    }

    renderFlavours() {
      const container = this.querySelector('.cake-atelier__choice-grid--flavours');
      if (!container || !this.configuration) return;
      container.replaceChildren();
      this.configuration.flavours.forEach((flavour, index) => {
        const label = document.createElement('label');
        label.className = 'cake-atelier__choice cake-atelier__choice--flavour';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'flavour';
        input.value = flavour.label;
        input.dataset.surchargeSar = flavour.surcharge_sar;
        input.dataset.label = this.localizedMapValue('flavours', flavour.label, flavour.label);
        input.checked = index === 0;
        const iconUrl = this.copy.assets && this.copy.assets.flavourIcons && this.copy.assets.flavourIcons[flavour.label];
        const text = document.createElement('span');
        text.textContent = input.dataset.label;
        label.append(input);
        if (iconUrl) {
          const icon = document.createElement('span');
          icon.className = 'cake-atelier__flavour-icon';
          icon.style.setProperty('--flavour-icon', `url("${iconUrl}")`);
          icon.setAttribute('aria-hidden', 'true');
          label.append(icon);
        }
        label.append(text);
        container.append(label);
      });
    }

    renderHeights() {
      const select = this.form.elements.cake_height;
      if (!select || !this.configuration) return;
      select.replaceChildren();
      this.configuration.heights.forEach((height) => {
        const option = document.createElement('option');
        option.value = height.cm;
        option.textContent = this.measurement(height.cm);
        option.dataset.standardPriceSar = height.standard_price_sar;
        option.dataset.decoratedPriceSar = height.decorated_price_sar;
        option.dataset.fiberPricePerPersonSar = height.fiber_price_per_person_sar;
        option.selected = height.cm === 25;
        select.append(option);
      });
    }

    renderFinishes(finishes) {
      const container = this.querySelector('[data-finish-options]');
      if (!container || !finishes.length) return;
      container.replaceChildren();
      finishes.forEach((finish, index) => {
        const label = document.createElement('label');
        const kindLabel = this.localizedMapValue('finishKinds', finish.kind, finish.kind);
        label.title = `${finish.label} · ${kindLabel}`;
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'color';
        input.value = finish.label;
        input.dataset.label = finish.label;
        input.dataset.color = finish.average_hex;
        input.dataset.finishKind = finish.kind;
        input.dataset.finishId = finish.id;
        input.checked = index === 0;
        if (index >= 20 && !this.finishesExpanded && !input.checked) label.hidden = true;
        input.setAttribute('aria-label', `${finish.label}, ${kindLabel}`);
        const swatch = document.createElement('span');
        swatch.style.setProperty('--swatch', finish.average_hex);
        swatch.style.backgroundImage = `url("${this.dataset.finishSprite}")`;
        swatch.style.backgroundSize = '1200% 600%';
        swatch.style.backgroundPosition = `${(finish.sprite_column / 11) * 100}% ${(finish.sprite_row / 5) * 100}%`;
        const number = document.createElement('small');
        number.textContent = String(finish.id).padStart(2, '0');
        label.append(input, swatch, number);
        container.append(label);
      });
      this.updateFinishToggle(finishes.length);
    }

    toggleFinishes() {
      this.finishesExpanded = !this.finishesExpanded;
      this.querySelectorAll('[data-finish-options] label').forEach((label, index) => {
        const input = label.querySelector('input');
        label.hidden = index >= 20 && !this.finishesExpanded && !(input && input.checked);
      });
      this.updateFinishToggle(this.querySelectorAll('[data-finish-options] label').length);
    }

    updateFinishToggle(count) {
      if (!this.swatchToggle) return;
      const hasMore = count > 20;
      this.swatchToggle.hidden = !hasMore;
      this.swatchToggle.setAttribute('aria-expanded', this.finishesExpanded ? 'true' : 'false');
      this.swatchToggle.textContent = this.finishesExpanded
        ? this.text('ui.show_fewer_finishes', 'Show fewer finishes')
        : this.text('ui.show_more_finishes', 'Show {{ count }} more finishes', { count: Math.max(0, count - 20) });
    }

    populateSizeOptions(shape) {
      const select = this.form.elements.cake_size;
      if (!select || !this.configuration) return;
      const sizes = this.configuration.shapes[shape] || [];
      select.replaceChildren();
      if (!sizes.length) {
        const option = document.createElement('option');
        option.value = 'Custom size';
        option.textContent = this.text('ui.custom_size', 'Custom size · quoted individually');
        option.dataset.label = option.textContent;
        option.dataset.maxDimensionCm = 20;
        select.append(option);
        return;
      }
      let defaultChosen = false;
      sizes.forEach((size) => {
        const option = document.createElement('option');
        option.value = size.label;
        option.textContent = this.localizeMeasurement(size.label);
        option.dataset.label = option.textContent;
        option.dataset.estimatedServings = size.estimated_servings;
        option.dataset.maxDimensionCm = Math.max(...size.dimensions_cm);
        option.dataset.dimensionsCm = size.dimensions_cm.join(',');
        option.dataset.basePriceSarVat = size.base_price_sar_vat;
        if (!defaultChosen && Math.max(...size.dimensions_cm) >= 20) {
          option.selected = true;
          defaultChosen = true;
        }
        select.append(option);
      });
    }

    selected(name) {
      const field = this.form.elements[name];
      if (!field) return '';
      if (field instanceof RadioNodeList) return field.value;
      return field.value;
    }

    labelForRadio(name) {
      const input = this.form.querySelector(`input[name="${name}"]:checked`);
      return input ? (input.dataset.label || input.value) : '';
    }

    selectedMany(name) {
      return Array.from(this.form.querySelectorAll(`input[name="${name}"]:checked`)).map((input) => input.dataset.label || input.value);
    }

    updatePreview() {
      const shape = this.selected('shape');
      const shapeLabel = this.labelForRadio('shape');
      const tiers = this.selected('tiers');
      const size = this.selectedLabel('cake_size') || this.text('ui.custom_size', 'Custom size');
      const height = this.selected('cake_height') || '25';
      const heightLabel = this.measurement(height);
      const flavour = this.labelForRadio('flavour');
      const colorInput = this.form.querySelector('input[name="color"]:checked');
      const color = colorInput ? (colorInput.dataset.label || colorInput.value) : this.text('ui.finish_fallback', 'Finish 01');
      const finishKind = colorInput ? colorInput.dataset.finishKind : 'solid';
      const finishId = colorInput ? Number(colorInput.dataset.finishId) : 1;
      this.cake.dataset.previewShape = shape;
      this.cake.dataset.previewTiers = tiers;
      this.cake.dataset.finishKind = [21, 24, 25, 30, 31, 37, 38, 63, 69].includes(finishId) ? 'marbled' : finishKind;
      const sizeOption = this.form.elements.cake_size && this.form.elements.cake_size.selectedOptions[0];
      const maxDimensionCm = Number(sizeOption && sizeOption.dataset.maxDimensionCm) || 20;
      const cakeScale = 0.64 + Math.min(0.34, (maxDimensionCm / 60) * 0.34);
      this.cake.style.setProperty('--cake-scale', cakeScale.toFixed(3));
      this.cake.style.setProperty('--tier-height-scale', (Number(height) / 25).toFixed(2));
      if (colorInput) this.cake.style.setProperty('--cake-color', colorInput.dataset.color);
      if (this.cake3d) {
        const dimensions = (sizeOption && sizeOption.dataset.dimensionsCm ? sizeOption.dataset.dimensionsCm : `${maxDimensionCm}`).split(',').map(Number).filter(Number.isFinite);
        this.cake3d.updateCake({
          shape,
          tiers: shape === 'tart' ? 1 : Number(tiers),
          dimensions,
          height: Number(height),
          color: colorInput ? colorInput.dataset.color : '#c6b2d8',
          finishKind,
          finishId
        });
        if (this.activeImageId) this.cake3d.setPlacement(this.placement, this.activeImageId);
      }
      const previewShape = this.localizedMapValue('shapeNames', shape, shape === 'round' ? 'round' : shapeLabel.toLowerCase());
      const tierText = shape === 'tart'
        ? this.localizedMapValue('shapeNames', 'tart', shapeLabel)
        : this.text('ui.preview_tiered_cake', '{{ tier }} {{ shape }} cake', { tier: this.tierCompoundLabel(tiers), shape: previewShape });
      this.querySelector('[data-preview-title]').textContent = tierText;
      this.querySelector('[data-preview-detail]').textContent = this.text('ui.preview_detail', '{{ size }} · {{ height }} high · {{ flavour }} · {{ finish }}', {
        size,
        height: heightLabel,
        flavour,
        finish: color
      });
      this.querySelector('[data-review-title]').textContent = this.text('ui.review_title', '{{ category }} · {{ shape }}', {
        category: this.selectedLabel('category'),
        shape: shapeLabel
      });
      this.querySelector('[data-review-detail]').textContent = this.text('ui.review_detail', '{{ tiers }} · {{ size }} · {{ height }} high · {{ flavour }} · {{ finish }}', {
        tiers: this.tierLabel(tiers),
        size,
        height: heightLabel,
        flavour,
        finish: color
      });
    }

    async handleImage(input) {
      const status = this.querySelector('.cake-atelier__upload-status');
      const files = Array.from(input.files || []);
      if (!files.length) return;
      const allowed = ['image/jpeg', 'image/png', 'image/webp'];
      const validFiles = files.filter((file) => allowed.includes(file.type) && file.size <= 8 * 1024 * 1024);
      if (!validFiles.length) {
        status.textContent = this.text('ui.file_type_error', 'Please choose JPG, PNG or WebP files under 8 MB.');
        return;
      }
      const availableSlots = Math.max(0, 5 - this.images.length);
      const selectedFiles = validFiles.slice(0, availableSlots);
      if (!selectedFiles.length) {
        status.textContent = this.text('ui.image_limit_error', 'You can place up to 5 images for this preview.');
        input.value = '';
        return;
      }

      for (const file of selectedFiles) {
        const image = {
          id: `image-${Date.now()}-${this.imageIdCounter += 1}`,
          name: file.name,
          file,
          url: URL.createObjectURL(file),
          placement: { tier: 'top', surface: 'front', x: 0, y: 0, scale: 0.42, rotation: 0 }
        };
        this.images.push(image);
        this.activeImageId = image.id;
        this.placement = { ...image.placement };
        if (this.cake3d) {
          try {
            await this.cake3d.setImage(image);
          } catch (error) {
            URL.revokeObjectURL(image.url);
            this.images = this.images.filter((item) => item.id !== image.id);
            status.textContent = this.text('ui.image_add_error', '{{ file }} could not be added to the 3D preview. Please try another file.', { file: file.name });
            console.error('[Cake atelier image]', error);
          }
        }
      }

      const active = this.activeImage();
      this.decal.style.backgroundImage = active ? `url("${active.url}")` : '';
      this.querySelector('[data-placement-panel]').hidden = false;
      this.syncTierOptions();
      this.syncPlacementControls();
      this.renderImageList();
      this.setCakeView('front');
      status.textContent = this.text('ui.images_added', '{{ count }} image(s) added. Select a thumbnail to place each one, then attach the originals in WhatsApp.', { count: selectedFiles.length });
      input.value = '';
      this.updatePlacement();
    }

    renderImageList() {
      if (!this.imageList) return;
      this.imageList.replaceChildren();
      this.images.forEach((image, index) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'cake-atelier__image-thumb';
        item.classList.toggle('is-active', image.id === this.activeImageId);
        item.dataset.imageId = image.id;
        item.setAttribute('aria-label', this.text('ui.image_thumb_label', 'Edit image {{ index }}: {{ name }}', { index: index + 1, name: image.name }));
        item.addEventListener('click', () => this.setActiveImage(image.id));
        const preview = document.createElement('img');
        preview.src = image.url;
        preview.alt = '';
        preview.loading = 'lazy';
        const name = document.createElement('span');
        name.textContent = `${index + 1}`;
        item.append(preview, name);
        this.imageList.append(item);
      });
      this.querySelector('[data-placement-panel]').hidden = !this.hasImages();
    }

    validate() {
      let valid = true;
      this.form.querySelectorAll('[required]').forEach((field) => {
        let invalid = !field.value.trim();
        if (field.name === 'date' && !invalid) invalid = !this.validFutureDate(field.value);
        field.classList.toggle('is-invalid', invalid);
        field.setAttribute('aria-invalid', invalid ? 'true' : 'false');
        if (invalid) valid = false;
      });
      if (!valid) this.form.querySelector('.is-invalid').focus();
      return valid;
    }

    formatDateField(field) {
      const digits = field.value.replace(/\D/g, '').slice(0, 8);
      field.value = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter(Boolean).join('/');
      field.classList.remove('is-invalid');
      field.setAttribute('aria-invalid', 'false');
    }

    validateDateField(field) {
      if (!field.value) return;
      const invalid = !this.validFutureDate(field.value);
      field.classList.toggle('is-invalid', invalid);
      field.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    }

    validFutureDate(value) {
      const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
      if (!match) return false;
      const day = Number(match[1]);
      const month = Number(match[2]);
      const year = Number(match[3]);
      const candidate = new Date(year, month - 1, day);
      if (candidate.getFullYear() !== year || candidate.getMonth() !== month - 1 || candidate.getDate() !== day) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return candidate >= today;
    }

    async uploadHostedAssets() {
      const endpoint = (this.dataset.uploadUrl || '').trim();
      if (!endpoint) return null;
      const files = this.images.map((image) => image.file).filter(Boolean);
      const previewBlob = this.cake3d
        ? await fetch(this.cake3d.exportPreview()).then((response) => response.blob())
        : null;
      if (!files.length && !previewBlob) return null;

      const status = this.querySelector('.cake-atelier__upload-status');
      status.textContent = this.text('ui.upload_preparing', 'Preparing hosted image links...');
      const payload = new FormData();
      payload.append('brief', JSON.stringify({
        category: this.selected('category'),
        shape: this.labelForRadio('shape'),
        event_date: this.selected('date'),
        customer_name: this.selected('customer_name')
      }));
      files.forEach((file, index) => payload.append('files', file, `inspiration-${index + 1}-${file.name}`));
      if (previewBlob) payload.append('files', previewBlob, `joy-cake-preview-${Date.now()}.png`);

      const response = await fetch(endpoint, {
        method: 'POST',
        body: payload,
        credentials: 'same-origin'
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.error) throw new Error(result.error || 'Hosted upload failed.');
      status.textContent = result.files && result.files.length ? this.text('ui.upload_finished', 'Hosted upload finished. Adding available links...') : '';
      return result;
    }

    hostedAssetLines(result) {
      if (!result || !Array.isArray(result.files) || !result.files.length) return [];
      const hostedFiles = result.files.filter((file) => file.url);
      const preview = hostedFiles.find((file) => file.role === 'preview');
      const inspirations = hostedFiles.filter((file) => file.role !== 'preview');
      return [
        preview ? this.text('whatsapp.hosted_preview', 'Hosted cake preview: {{ url }}', { url: preview.url }) : null,
        inspirations.length ? this.text('whatsapp.hosted_images', 'Hosted inspiration images: {{ urls }}', {
          urls: inspirations.map((file, index) => `${index + 1}. ${file.url}`).join(' | ')
        }) : null
      ].filter(Boolean);
    }

    async submit(event) {
      event.preventDefault();
      if (!this.validate()) return;
      const submitButton = this.querySelector('.cake-atelier__whatsapp');
      submitButton.disabled = true;
      this.saveActivePlacement();
      let hostedAssets = null;
      let hostedUploadFailed = false;
      try {
        hostedAssets = await this.uploadHostedAssets();
      } catch (error) {
        hostedUploadFailed = true;
        this.querySelector('.cake-atelier__upload-status').textContent = this.text('ui.upload_failed', 'Hosted image links are unavailable right now. Please attach the originals in WhatsApp.');
        console.error('[Cake atelier upload]', error);
      } finally {
        submitButton.disabled = false;
      }
      const imageNames = this.images.map((image, index) => `${index + 1}. ${image.name}`).join('; ');
      const hostedLines = this.hostedAssetLines(hostedAssets);
      if (hostedAssets && hostedLines.length) this.querySelector('.cake-atelier__upload-status').textContent = this.text('ui.upload_ready', 'Hosted image links are ready.');
      if (hostedAssets && !hostedLines.length) this.querySelector('.cake-atelier__upload-status').textContent = this.text('ui.upload_pending', 'Shopify is still preparing hosted links. Please attach the originals in WhatsApp.');
      const noValue = this.text('whatsapp.none', 'None');
      const noneSelected = this.text('whatsapp.none_selected', 'None selected');
      const notSpecified = this.text('whatsapp.not_specified', 'Not specified');
      const lines = [
        this.text('whatsapp.greeting', 'Hello Joy Chocolate, I would like a quote for a custom cake.'),
        '',
        `${this.text('whatsapp.name', 'Name')}: ${this.selected('customer_name')}`,
        `${this.text('whatsapp.mobile', 'Mobile')}: ${this.selected('phone')}`,
        `${this.text('whatsapp.category', 'Category')}: ${this.selectedLabel('category')}`,
        `${this.text('whatsapp.shape', 'Shape')}: ${this.labelForRadio('shape')}`,
        `${this.text('whatsapp.tiers', 'Tiers')}: ${this.tierLabel()}`,
        `${this.text('whatsapp.size', 'Size')}: ${this.selectedLabel('cake_size')}`,
        `${this.text('whatsapp.height', 'Height')}: ${this.measurement(this.selected('cake_height'))}`,
        `${this.text('whatsapp.flavour', 'Flavour')}: ${this.labelForRadio('flavour')}`,
        `${this.text('whatsapp.finish', 'Finish')}: ${this.selectedLabel('color') || this.selected('color')}`,
        `${this.text('whatsapp.image_placement', 'Image placement')}: ${this.placementDescription()}`,
        `${this.text('whatsapp.custom_construction', 'Custom construction')}: ${this.selectedMany('custom_work').join(', ') || noneSelected}`,
        `${this.text('whatsapp.special_requests', 'Special requests')}: ${this.selected('special_requests') || noValue}`,
        `${this.text('whatsapp.occasion', 'Occasion')}: ${this.selected('occasion') || notSpecified}`,
        `${this.text('whatsapp.event_date', 'Event date')}: ${this.selected('date')}`,
        `${this.text('whatsapp.cake_message', 'Cake message')}: ${this.selected('message') || noValue}`,
        `${this.text('whatsapp.delivery', 'Delivery')}: ${this.selectedLabel('fulfilment')}`,
        imageNames
          ? `${this.text('whatsapp.inspiration_images', 'Inspiration images')}: ${imageNames}${hostedLines.length ? '' : ` (${this.text('whatsapp.attach_originals', 'I will attach the originals in this chat')})`}`
          : `${this.text('whatsapp.inspiration_images', 'Inspiration images')}: ${noValue}`,
        ...hostedLines,
        hostedUploadFailed ? this.text('whatsapp.hosted_unavailable', 'Hosted upload: unavailable, I will attach the originals in this chat.') : null,
        '',
        this.text('whatsapp.closing', 'Please confirm availability, design feasibility and pricing.')
      ].filter(Boolean);
      const phone = (this.dataset.phone || '').replace(/\D/g, '');
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(lines.join('\n'))}`, '_blank', 'noopener,noreferrer');
    }
  });
}
